import * as React from 'react';

import { FETCH_MORE_COMPONENT_LIMIT } from 'bundles/page-config-common/constants/pagination';
import {
  useConfigFetchMore,
  useConfigPagination,
  useLoading,
} from 'bundles/page-config-common/contexts/ComponentsConfigContext';
import pageConfigFetchMore from 'bundles/page-config-common/utils/pageConfigFetchMore';

/*
Enables infinite scroll pagination for queries to the page config component service.
Returns a tuple with 0 index being a ref for the target element that triggers new requests (place this below all your rendered components from config).
1 index is a boolean that indicates if the fetch more request is loading, use this to display a loading state.

To utilize this, ensure you are using the default page config component provider - bundles/page-config-common/providers/ComponentsConfigProvider -
call this hook in a Component Config Renderer component and attach the ref to your target element. Scrolling to this ref will trigger a fetch more request.
Fetch more results will get merged with existing data, see pageConfigFetchMore for more details.

Initial limit for config components is set as a prop in the ComponentsConfigProvider and can be adjusted on page by page basis. 
*/
const useComponentConfigPagination = (
  limit = FETCH_MORE_COMPONENT_LIMIT,
  // Config renderers can be used in nested contexts and those nested usages need to disable pagination.
  disable?: boolean
): [React.RefObject<HTMLDivElement>, boolean | undefined] => {
  const configPagination = useConfigPagination();
  const configFetchMore = useConfigFetchMore();
  const loading = useLoading();
  const targetElementRef = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          !disable &&
          // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          entry.isIntersecting &&
          configFetchMore &&
          !loading?.isFetchMoreLoading &&
          !loading?.isInitiallyLoading &&
          !!configPagination?.cursor
        ) {
          pageConfigFetchMore(limit, configFetchMore, configPagination);
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    );

    if (targetElementRef.current && !disable) {
      observer.observe(targetElementRef.current);
    }

    return () => observer.disconnect();
  }, [targetElementRef, configFetchMore, configPagination, loading, limit, disable]);

  return [targetElementRef, loading?.isFetchMoreLoading];
};

export default useComponentConfigPagination;
